import React, { forwardRef, useCallback, useEffect, useMemo, useState } from "react";

import { Box, Button, CircularProgress, LinearProgress, Typography } from "@mui/material";
import { BenchmarkComparisonCard } from "components/Report-generator/components/cards/BenchmarkComparisonCard";
import { CashFlowTimelineCard } from "components/Report-generator/components/cards/CashFlowTimelineCard";
import { ChartCard } from "components/Report-generator/components/cards/ChartCard";
import { DataFreshnessCard } from "components/Report-generator/components/cards/DataFreshnessCard";
import { DataTableCard } from "components/Report-generator/components/cards/DataTableCard";
import { ExposureBreakdownCard } from "components/Report-generator/components/cards/ExposureBreakdownCard";
import { AICard } from "components/Report-generator/components/cards/AICard";
import { KpiCard } from "components/Report-generator/components/cards/KpiCard";
import { MacroBriefCard } from "components/Report-generator/components/cards/MacroBriefCard";
import { PerformanceAttributionCard } from "components/Report-generator/components/cards/PerformanceAttributionCard";
import { SummaryCard } from "components/Report-generator/components/cards/SummaryCard";
import { TextCard } from "components/Report-generator/components/cards/TextCard";
import { UnfundedCommitmentsCard } from "components/Report-generator/components/cards/UnfundedCommitmentsCard";
import { CardSizeProvider } from "components/Report-generator/context/CardSizeContext";
import { TableExpandProvider } from "components/Report-generator/context/TableExpandContext";
import { getPageMetrics } from "components/Report-generator/utils/pageMetrics";
import { splitPagesForOverflow } from "components/Report-generator/utils/splitTablePages";
import { dateRangeOptions } from "constants/index";
import type {
  BenchmarkComparisonCardData,
  CanvasCard,
  CashFlowTimelineCardData,
  Chart,
  Currency,
  DataFreshnessCardData,
  DataTableCardData,
  DateRangeValue,
  ExposureCardData,
  Filters,
  KPICardData,
  MacroBriefCardData,
  PerformanceAttributionCardData,
  ReportMeta,
  ReportOrientation,
  ReportPage,
  SummaryCardData,
  TableCardConfig,
  UnfundedCardData
} from "store/types";

const EMPTY_SET: Set<string> = new Set();

interface Props {
  cards: CanvasCard[];
  pages: ReportPage[];
  reportMeta: ReportMeta;
  period: DateRangeValue | null;
  currency: Currency;
  orientation: ReportOrientation;
  filters?: Filters;
  expandedRows: Record<string, Set<string>>;
  onToggleExpand: (cardId: string, rowKey: string) => void;
  onBack: () => void;
  onExportPDF: () => Promise<void>;
  autoExport?: boolean;
}

const PreviewMode = forwardRef<HTMLDivElement, Props>(
  (
    {
      cards,
      pages,
      reportMeta,
      period,
      currency,
      orientation,
      filters,
      expandedRows,
      onToggleExpand,
      onBack,
      onExportPDF,
      autoExport
    },
    ref
  ) => {
    const { width: pageWidth, height: pageHeight } = getPageMetrics(orientation);
    const [exporting, setExporting] = useState(false);

    const handleExport = useCallback(async () => {
      setExporting(true);
      try {
        await onExportPDF();
      } finally {
        setExporting(false);
      }
    }, [onExportPDF]);

    useEffect(() => {
      if (!autoExport) return undefined;
      const timer = window.setTimeout(() => {
        void handleExport();
      }, 500);
      return () => window.clearTimeout(timer);
    }, [autoExport, handleExport]);

    const asOfFormatted = formatDate(reportMeta.asOfDate);
    const generatedOn = formatDate(new Date().toISOString());

    const { groupedPages, resolvedPageMeta, pageCount, cardOffsets, originalCardIds } = useMemo(() => {
      const rawGroupedPages: CanvasCard[][] = Array.from({ length: pages.length }, (_, i) =>
        cards.filter((c) => c.page === i)
      );
      const result = splitPagesForOverflow(rawGroupedPages, pages, pageHeight, pageWidth, expandedRows);
      return {
        groupedPages: result.pages,
        resolvedPageMeta: result.pageMeta,
        pageCount: result.pages.length,
        cardOffsets: result.cardOffsets,
        originalCardIds: result.originalCardIds
      };
    }, [cards, pages, pageHeight, pageWidth, expandedRows]);

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "calc(100vh - 120px)",
          bgcolor: "#F5F7FA",
          overflowX: "auto"
        }}
      >
        {!autoExport && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              height: 60,
              bgcolor: "#0F5D5E",
              display: "flex",
              alignItems: "center",
              p: "0 24px 0 80px",
              gap: 2,
              zIndex: 1500,
              boxShadow: "0 2px 8px rgba(0,0,0,0.2)"
            }}
          >
            <Button
              onClick={onBack}
              sx={{
                border: "1px solid rgba(255,255,255,0.28)",
                borderRadius: "999px",
                p: "7px 14px",
                color: "#FFFFFF",
                fontSize: 13,
                fontWeight: 600,
                textTransform: "none",
                "&:hover": { bgcolor: "rgba(255,255,255,0.1)" }
              }}
            >
              Back to editor
            </Button>
            <Typography
              sx={{ flex: 1, textAlign: "center", color: "rgba(255,255,255,0.78)", fontSize: 12, fontWeight: 600 }}
            >
              Preview Mode | {reportMeta.title} ({pageCount} page{pageCount !== 1 ? "s" : ""})
            </Typography>
            <Button
              onClick={handleExport}
              disabled={exporting}
              sx={{
                borderRadius: "999px",
                p: "8px 16px",
                bgcolor: exporting ? "rgba(255,255,255,0.15)" : "#FFFFFF",
                color: exporting ? "#FFFFFF" : "#0F5D5E",
                fontSize: 13,
                fontWeight: 700,
                textTransform: "none",
                minWidth: 130,
                border: exporting ? "1px solid rgba(255,255,255,0.3)" : "none",
                transition: "all 0.3s ease",
                "&:hover": { bgcolor: exporting ? "rgba(255,255,255,0.15)" : "#E8F5F0" },
                "&.Mui-disabled": { color: "#FFFFFF" }
              }}
            >
              {exporting ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <CircularProgress size={14} sx={{ color: "#FFFFFF" }} />
                  Generating...
                </Box>
              ) : (
                "Export PDF"
              )}
            </Button>
          </Box>
        )}

        <Box
          ref={ref}
          sx={{ width: pageWidth, margin: autoExport ? "0 auto" : "80px auto 48px", position: "relative" }}
        >
          {exporting && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 1400,
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                pt: "30vh",
                bgcolor: "rgba(245, 247, 250, 0.65)",
                backdropFilter: "blur(4px)"
              }}
            >
              <Box
                sx={{
                  bgcolor: "#FFFFFF",
                  borderRadius: "16px",
                  p: "32px 40px",
                  boxShadow: "0 16px 48px rgba(15, 93, 94, 0.15), 0 4px 12px rgba(0,0,0,0.06)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "16px",
                  minWidth: 260
                }}
              >
                <CircularProgress size={32} thickness={4} sx={{ color: "#0F5D5E" }} />
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#071f35" }}>Generating PDF</Typography>
                <Typography sx={{ fontSize: 12, color: "#6B7280" }}>
                  Capturing {pageCount} page{pageCount !== 1 ? "s" : ""}...
                </Typography>
                <LinearProgress
                  sx={{
                    width: "100%",
                    height: 4,
                    borderRadius: 2,
                    bgcolor: "#E5E7EB",
                    "& .MuiLinearProgress-bar": {
                      bgcolor: "#0F5D5E",
                      borderRadius: 2
                    }
                  }}
                />
              </Box>
            </Box>
          )}
          {groupedPages.map((pageCards, pageIndex) => {
            const page = resolvedPageMeta[pageIndex];
            const layout = page.layout;
            const isTitlePage = layout === "title";
            const isSectionBreak = layout === "section";
            const showHeader = layout === "header-content";

            return (
              <Box
                key={pageIndex}
                data-pdf-page={pageIndex}
                sx={{
                  width: pageWidth,
                  height: pageHeight,
                  bgcolor: "#FFFFFF",
                  boxShadow: autoExport ? "none" : "0 8px 28px rgba(15,23,42,0.12)",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  mb: pageIndex < pageCount - 1 ? (autoExport ? 0 : "24px") : 0,
                  ...(autoExport && pageIndex < pageCount - 1 ? { pageBreakAfter: "always" } : {})
                }}
              >
                {isTitlePage && (
                  <Box
                    sx={{
                      bgcolor: "#071f35",
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      p: "0 48px"
                    }}
                  >
                    {reportMeta.logo && (
                      <Box
                        sx={{
                          width: 88,
                          height: 88,
                          borderRadius: "16px",
                          mb: 4,
                          bgcolor: "rgba(255,255,255,0.12)",
                          overflow: "hidden",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}
                      >
                        <Box
                          component="img"
                          src={reportMeta.logo}
                          alt="Logo"
                          sx={{ width: "100%", height: "100%", objectFit: "contain" }}
                        />
                      </Box>
                    )}
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: "rgba(255,255,255,0.45)",
                        textTransform: "uppercase",
                        letterSpacing: "0.12em",
                        mb: "14px"
                      }}
                    >
                      Portfolio Report
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 36,
                        fontWeight: 700,
                        color: "#FFFFFF",
                        lineHeight: 1.2,
                        textAlign: "center",
                        maxWidth: 700,
                        overflowWrap: "break-word",
                        wordBreak: "break-word"
                      }}
                    >
                      {reportMeta.title}
                    </Typography>
                    {reportMeta.subtitle && (
                      <Typography
                        sx={{
                          fontSize: 16,
                          color: "rgba(255,255,255,0.68)",
                          mt: "12px",
                          textAlign: "center",
                          overflowWrap: "break-word",
                          wordBreak: "break-word"
                        }}
                      >
                        {reportMeta.subtitle}
                      </Typography>
                    )}
                    <Box
                      sx={{
                        mt: 6,
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                        alignItems: "center",
                        justifyContent: "center"
                      }}
                    >
                      <PreviewMetaField label="As of Date" value={asOfFormatted} />
                      {reportMeta.preparedBy && (
                        <PreviewMetaField label="Prepared by" value={reportMeta.preparedBy} />
                      )}
                    </Box>
                  </Box>
                )}

                {isSectionBreak && (
                  <Box sx={{ bgcolor: "#071f35", p: "48px 48px 36px", flexShrink: 0 }}>
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: "rgba(255,255,255,0.45)",
                        textTransform: "uppercase",
                        letterSpacing: "0.1em",
                        mb: 1
                      }}
                    >
                      Section
                    </Typography>
                    <Typography sx={{ fontSize: 28, fontWeight: 700, color: "#FFFFFF", lineHeight: 1.25 }}>
                      {page.title || "Untitled Section"}
                    </Typography>
                  </Box>
                )}

                {showHeader && (
                  <Box
                    sx={{
                      bgcolor: "#071f35",
                      p: "36px 48px",
                      display: "flex",
                      alignItems: "flex-end",
                      justifyContent: "space-between",
                      gap: 2,
                      flexShrink: 0
                    }}
                  >
                    {reportMeta.logo && (
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          flexShrink: 0,
                          borderRadius: "10px",
                          bgcolor: "rgba(255,255,255,0.12)",
                          overflow: "hidden",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}
                      >
                        <Box
                          component="img"
                          src={reportMeta.logo}
                          alt="Logo"
                          sx={{ width: "100%", height: "100%", objectFit: "contain" }}
                        />
                      </Box>
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: "rgba(255,255,255,0.55)",
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                          mb: 1
                        }}
                      >
                        Portfolio Report
                      </Typography>
                      <Typography sx={{ fontSize: 26, fontWeight: 700, color: "#FFFFFF", lineHeight: 1.2 }}>
                        {reportMeta.title}
                      </Typography>
                      {reportMeta.subtitle && (
                        <Typography sx={{ fontSize: 14, color: "rgba(255,255,255,0.68)", mt: "6px" }}>
                          {reportMeta.subtitle}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.55)", mb: "4px" }}>
                        As of Date
                      </Typography>
                      <Typography sx={{ fontSize: 16, fontWeight: 600, color: "#FFFFFF" }}>{asOfFormatted}</Typography>
                      <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.45)", mt: "4px" }}>
                        Period:{" "}
                        {period?.value
                          ? (dateRangeOptions.find((opt) => opt.value === period.value)?.text ?? period.value)
                          : "N/A"}{" "}
                        | Currency: {currency}
                      </Typography>
                      {filters && (filters.investors.length > 0 || filters.assetClasses.length > 0) && (
                        <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.45)", mt: "2px" }}>
                          {filters.investors.length > 0 && `Investor (${filters.investors.length})`}
                          {filters.investors.length > 0 && filters.assetClasses.length > 0 && " | "}
                          {filters.assetClasses.length > 0 && `Asset Class (${filters.assetClasses.length})`}
                        </Typography>
                      )}
                      {reportMeta.preparedBy && (
                        <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.45)", mt: "2px" }}>
                          Prepared by: {reportMeta.preparedBy}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}

                {!isTitlePage && (
                  <Box
                    sx={{
                      p: "32px 48px 48px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "20px",
                      flex: 1,
                      minHeight: 0,
                      overflow: "hidden"
                    }}
                  >
                    {(() => {
                      const rows: CanvasCard[][] = [];
                      let currentRow: CanvasCard[] = [];
                      let rowW = 0;
                      for (const card of pageCards) {
                        if (rowW > 0 && rowW + card.widthPercent > 100.01) {
                          rows.push([...currentRow]);
                          currentRow = [];
                          rowW = 0;
                        }
                        currentRow.push(card);
                        rowW += card.widthPercent;
                      }
                      if (currentRow.length > 0) rows.push(currentRow);
                      return rows.map((rowCards, rowIdx) => (
                        <Box key={rowIdx} sx={{ display: "flex", gap: "20px", alignItems: "flex-start" }}>
                          {rowCards.map((card) => {
                            const origId = originalCardIds[card.id] ?? card.id;
                            const expandValue = {
                              expanded: expandedRows[origId] ?? EMPTY_SET,
                              onToggle: (rowKey: string) => onToggleExpand(origId, rowKey),
                              indexOffsets: cardOffsets[card.id]
                            };
                            return (
                              <TableExpandProvider key={card.id} value={expandValue}>
                                <PreviewCardWrapper card={card} orientation={orientation} rowSize={rowCards.length} />
                              </TableExpandProvider>
                            );
                          })}
                        </Box>
                      ));
                    })()}
                  </Box>
                )}

                <Box
                  sx={{
                    borderTop: "1px solid #F3F4F6",
                    p: "8px 48px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    flexShrink: 0,
                    mt: "auto"
                  }}
                >
                  <Typography sx={{ fontSize: 11, color: "#9FB3C8" }}>
                    {pageCount > 1 ? `Page ${pageIndex + 1} of ${pageCount} | ` : ""}
                    Generated {generatedOn}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  }
);

PreviewMode.displayName = "PreviewMode";

interface PreviewCardWrapperProps {
  card: CanvasCard;
  orientation: ReportOrientation;
  rowSize: number;
}

const PreviewCardWrapper: React.FC<PreviewCardWrapperProps> = ({ card, orientation, rowSize }) => {
  const cardSizeCtx = useMemo(
    () => ({
      widthPercent: card.widthPercent,
      heightPx: card.heightPx,
      topOffsetPx: card.topOffsetPx,
      leftOffsetPx: card.leftOffsetPx,
      orientation,
      isEditMode: false
    }),
    [card.widthPercent, card.heightPx, card.topOffsetPx, card.leftOffsetPx, orientation]
  );

  return (
    <CardSizeProvider value={cardSizeCtx}>
      <Box
        sx={{
          flex: "0 0 auto",
          width: `calc(${card.widthPercent}% - ${((rowSize - 1) * 20) / rowSize}px)`,
          minWidth: 0,
          bgcolor: "#FFFFFF",
          borderRadius: "8px",
          boxShadow: "0 1px 4px rgba(0,0,0,0.07), 0 2px 12px rgba(0,0,0,0.04)",
          border: "1px solid #F3F4F6",
          overflow: "hidden",
          position: "relative",
          top: card.topOffsetPx ?? 0,
          left: card.leftOffsetPx ?? 0,
          ...(card.heightPx != null ? { height: card.heightPx } : {})
        }}
      >
        {card.config.type === "kpi" && <KpiCard data={card.data as KPICardData | undefined} loading={card.loading} />}
        {card.config.type === "summary" && (
          <SummaryCard data={card.data as SummaryCardData | undefined} loading={card.loading} />
        )}
        {card.config.type === "chart" && (
          <ChartCard data={card.data as Chart | undefined} loading={card.loading} animation={false} />
        )}
        {card.config.type === "table" && (
          <DataTableCard
            data={card.data as DataTableCardData | undefined}
            loading={card.loading}
            columnSettings={(card.config as TableCardConfig).columns}
            columnWidths={(card.config as TableCardConfig).columnWidths}
          />
        )}
        {card.config.type === "text" && <TextCard config={card.config} isPreview />}
        {card.config.type === "ai_card" && <AICard config={card.config} data={card.data} isPreview />}
        {card.config.type === "exposure" && (
          <ExposureBreakdownCard
            config={card.config}
            data={card.data as ExposureCardData | undefined}
            loading={card.loading}
          />
        )}
        {card.config.type === "cashflow" && (
          <CashFlowTimelineCard data={card.data as CashFlowTimelineCardData[] | undefined} />
        )}
        {card.config.type === "unfunded" && (
          <UnfundedCommitmentsCard data={card.data as UnfundedCardData[] | undefined} />
        )}
        {card.config.type === "performance" && (
          <PerformanceAttributionCard
            config={card.config}
            data={card.data as PerformanceAttributionCardData | undefined}
            loading={card.loading}
          />
        )}
        {card.config.type === "benchmark" && (
          <BenchmarkComparisonCard
            config={card.config}
            data={card.data as BenchmarkComparisonCardData | undefined}
            loading={card.loading}
          />
        )}
        {card.config.type === "dataFreshness" && (
          <DataFreshnessCard data={card.data as DataFreshnessCardData | undefined} />
        )}
        {card.config.type === "macroBrief" && (
          <MacroBriefCard data={card.data as MacroBriefCardData | undefined} loading={card.loading} />
        )}
      </Box>
    </CardSizeProvider>
  );
};

interface PreviewMetaFieldProps {
  label: string;
  value: string;
}

const PreviewMetaField: React.FC<PreviewMetaFieldProps> = ({ label, value }) => {
  return (
    <Box sx={{ textAlign: "center" }}>
      <Typography sx={{ fontSize: 10, fontWeight: 600, color: "rgba(255,255,255,0.45)", mb: "4px" }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 16, fontWeight: 600, color: "#FFFFFF" }}>{value}</Typography>
    </Box>
  );
};

function formatDate(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

export { PreviewMode };
